import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";

export default function FAQGrid({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "#111827";
    const fontFamily = content.fontFamily || "inherit";

    const items = content.items || [];

    const updateItem = (index: number, key: string, value: string) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [key]: value };
        onContentChange?.({ ...content, items: newItems });
    };

    return (
        <section className={`px-6 py-24 bg-white ${isSelected ? "ring-2 ring-blue-500" : ""}`} style={{ fontFamily }}>
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
                <div>
                    <h2 className="text-5xl font-black text-gray-900 tracking-tight mb-8">
                        <EditableText
                            value={content.title}
                            onChange={(v) => onContentChange?.({ ...content, title: v })}
                            as="span"
                            placeholder="FAQ Title"
                        />
                    </h2>
                    <p className="text-xl text-gray-500 mb-10">
                        <EditableText
                            value={content.subtitle || "Everything you need to know about the product and billing."}
                            onChange={(v) => onContentChange?.({ ...content, subtitle: v })}
                            as="span"
                            placeholder="Subtitle goes here..."
                        />
                    </p>
                    <button className="px-8 py-4 rounded-xl font-bold text-white transition-opacity hover:opacity-90" style={{ backgroundColor: primaryColor }}>
                        <EditableText
                            value={content.ctaText || "Contact Support"}
                            onChange={(v) => onContentChange?.({ ...content, ctaText: v })}
                            as="span"
                            placeholder="Contact Button Text"
                        />
                    </button>
                </div>
                <div className="space-y-10">
                    {items.map((item: any, i: number) => (
                        <div key={i}>
                            <h4 className="text-xl font-bold text-gray-900 mb-3">
                                <EditableText
                                    value={item.q}
                                    onChange={(v) => updateItem(i, 'q', v)}
                                    as="span"
                                    placeholder="Question?"
                                />
                            </h4>
                            <p className="text-gray-600 leading-relaxed">
                                <EditableText
                                    value={item.a}
                                    onChange={(v) => updateItem(i, 'a', v)}
                                    as="span"
                                    placeholder="Answer..."
                                />
                            </p>
                            {item.link && (
                                <a
                                    href={onContentChange ? undefined : item.link}
                                    target={item.link.startsWith('http') && !onContentChange ? "_blank" : undefined}
                                    rel={item.link.startsWith('http') && !onContentChange ? "noopener noreferrer" : undefined}
                                    className="inline-block mt-3 text-sm font-bold text-blue-600 hover:text-blue-700 hover:underline"
                                    onClick={(e) => {
                                        if (onContentChange) e.preventDefault();
                                    }}
                                >
                                    <EditableText
                                        value={item.linkText || "Learn more"}
                                        onChange={(v) => updateItem(i, 'linkText', v)}
                                        as="span"
                                        placeholder="Link text"
                                    />
                                </a>
                            )}
                        </div>
                    ))}
                    {isSelected && (
                        <button
                            onClick={() => {
                                const newItems = [...items, { q: "New Question?", a: "New Answer" }];
                                onContentChange?.({ ...content, items: newItems });
                            }}
                            className="w-full py-4 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-colors font-medium flex items-center justify-center gap-2"
                        >
                            + Add Question
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
}
